<div class="flex flex-col gap-2">
  <!-- Above whatever the basket came back with: nothing on the shelves nearby is exactly when the
       area needs moving or widening, so the control cannot live inside a branch of the answer. -->
  <guarantees-search-area
    [area]="searchArea.area()"
    [radius]="searchArea.radiusMeters()"
    (placed)="searchArea.place($event)"
    (radiusPicked)="searchArea.setRadius($event)"
  ></guarantees-search-area>

  @if (awaitingArea()) {
    <!-- Nothing has been asked of anyone: the map below is the question, not an answer to it. -->
    <p class="flex items-center gap-1.5 text-sm text-neutral-500">
      <mat-icon class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400">place</mat-icon>
      {{ "guaranteeV2.message.pickSearchArea" | translate }}
    </p>
  } @else if (loading()) {
    <!-- Said on the map where the map is up; only the list view needs a line of its own. -->
    @if (!showMap()) {
      <div class="flex items-center gap-2 px-2 py-3 text-sm text-neutral-400">
        <guarantees-spinner></guarantees-spinner>
        {{ "guaranteeV2.message.searchingPharmacies" | translate }}
      </div>
    }
  } @else if (failed()) {
    <guarantees-callout
      tone="warning"
      icon="cloud_off"
      stack
    >
      {{ "guaranteeV2.message.pharmaciesUnavailable" | translate }}
      <button
        class="self-start rounded-full border border-warning-300 px-2 py-0.5 text-sm font-semibold text-warning-800 hover:bg-warning-100 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
        type="button"
        (click)="retry()"
      >
        {{ "guaranteeV2.action.retry" | translate }}
      </button>
    </guarantees-callout>
  } @else if (empty()) {
    <guarantees-callout
      tone="warning"
      icon="local_pharmacy"
      stack
    >
      <span>{{ "guaranteeV2.message.noPharmacies" | translate }}</span>
      <span>{{ "guaranteeV2.message.widenSearchArea" | translate }}</span>
    </guarantees-callout>
  } @else {
    @if (choiceLapsed()) {
      <guarantees-callout
        tone="warning"
        icon="local_pharmacy"
      >
        {{ "guaranteeV2.message.pharmacyLapsed" | translate }}
      </guarantees-callout>
    }

    <!-- A district answers with every counter that stocks the drugs; naming the one wanted is
         faster than scrolling past the rest. -->
    @if (filterable()) {
      <div class="flex flex-col gap-1.5">
        <guarantees-search-field
          icon="storefront"
          name="pharmacySearch"
          [ariaLabel]="'guaranteeV2.message.searchPharmacy' | translate"
          [placeholder]="'guaranteeV2.message.searchPharmacy' | translate"
          [value]="term()"
          (valueChange)="type($event)"
        ></guarantees-search-field>

        @if (narrowsByHours()) {
          <button
            guaranteesToggle
            shape="pill"
            size="xs"
            type="button"
            class="self-start"
            [pressed]="openOnly()"
            (click)="toggleOpen()"
          >
            <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">schedule</mat-icon>
            {{ "guaranteeV2.action.onlyOpenPharmacies" | translate }}
          </button>
        }
      </div>
    }

    <!-- What the basket left standing, and the one switch between its two readings — standing
         apart from the branches below, so it never jumps while a filter is being typed. -->
    <span class="flex items-center gap-2">
      <span class="min-w-0 flex-1 text-sm text-neutral-400">{{ "guaranteeV2.message.pharmaciesFound" | translate: { count: orderableCount() } }}</span>

      <!-- One basket, two ways of reading it: by what each counter stocks, or by where they are. -->
      @if (locatable()) {
        <span [class]="segmentedGroup">
          @for (option of views; track option.id) {
            <button
              guaranteesToggle
              type="button"
              size="sm"
              shape="segment"
              [pressed]="(option.id === 'map') === showMap()"
              (click)="view.set(option.id)"
            >
              <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">{{ option.icon }}</mat-icon>
              {{ option.labelKey | translate }}
            </button>
          }
        </span>
      }
    </span>

    <!-- The narrowing found nothing, which is not the shelves being bare: the box that was typed
         into stays where it is, with the way back to the whole basket beside it. -->
    @if (noMatches()) {
      <guarantees-callout
        tone="warning"
        icon="search_off"
        stack
      >
        {{ "guaranteeV2.message.noSearchResults" | translate }}
        <button
          class="self-start rounded-full border border-warning-300 px-2 py-0.5 text-sm font-semibold text-warning-800 hover:bg-warning-100 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
          type="button"
          (click)="clearFilter()"
        >
          {{ "guaranteeV2.action.clearSearch" | translate }}
        </button>
      </guarantees-callout>
    } @else if (!showMap() && matchCount() > 0) {
      <ul class="flex max-h-80 flex-col gap-1.5 overflow-y-auto">
        @for (row of rows(); track row.pharmacy.id) {
          <li [attr.data-pharmacy]="row.pharmacy.id">
            <div [class]="row.className">
              <button
                class="flex w-full items-start gap-2 px-2.5 py-2 text-left focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:cursor-not-allowed"
                type="button"
                [disabled]="!!row.refusalKey"
                (click)="toggle(row)"
              >
                @if (row.holdsChoice) {
                  <mat-icon class="mt-0.5 h-4! w-4! shrink-0 text-base! leading-4! text-primary-600">check_circle</mat-icon>
                } @else if (row.pharmacy.logoUrl) {
                  <img
                    class="h-7 w-7 shrink-0 rounded-md border border-neutral-100 bg-neutral-0 object-contain p-0.5"
                    [class.opacity-40]="!!row.refusalKey"
                    [src]="row.pharmacy.logoUrl"
                    alt=""
                    #pharmacyLogo
                    (error)="pharmacyLogo.hidden = true"
                  />
                } @else {
                  <mat-icon class="mt-0.5 h-4! w-4! shrink-0 text-base! leading-4! text-neutral-300">local_pharmacy</mat-icon>
                }

                <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span
                    class="min-w-0 truncate text-sm font-semibold"
                    [class]="row.refusalKey ? 'text-neutral-400' : 'text-neutral-700'"
                    >{{ row.pharmacy.pharmacyName }}</span
                  >
                  <span class="min-w-0 truncate text-sm text-neutral-500">{{ row.pharmacy.pharmacyAddress }}</span>

                  <span class="flex flex-wrap items-center gap-x-2 text-sm text-neutral-400">
                    <span class="tabular-nums">{{ "guaranteeV2.message.distanceMeters" | translate: { count: row.pharmacy.distance } }}</span>
                    <span [class]="row.pharmacy.isOpenNow ? 'text-success-600' : ''">{{ row.pharmacy.scheduleLabel }}</span>
                  </span>

                  <!-- Said in the row rather than behind a hover: a row that cannot be clicked cannot be hovered. -->
                  @if (row.refusalKey) {
                    <span class="flex items-center gap-1 text-sm text-neutral-400">
                      <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">block</mat-icon>
                      {{ row.refusalKey | translate }}
                    </span>
                  }
                </span>

                @if (!row.refusalKey) {
                  <mat-icon class="mt-0.5 h-4! w-4! shrink-0 text-base! leading-4! text-neutral-300">{{ row.expanded ? "expand_less" : "expand_more" }}</mat-icon>
                }
              </button>

              <!-- The pharmacy's own pick already stands; the alternatives are for when it is wrong. -->
              @if (row.expanded) {
                <div class="flex flex-col gap-2 border-t border-neutral-100 px-2.5 py-2">
                  @for (item of row.pharmacy.prescriptionItems; track item.prescriptionId) {
                    <div class="flex flex-col gap-1">
                      <span class="flex flex-wrap items-baseline gap-x-2">
                        <span class="text-sm font-semibold text-neutral-700">{{ item.identifier }}</span>
                        <span class="text-sm text-neutral-400">{{ item.form }}</span>
                      </span>

                      <div class="flex flex-col gap-1">
                        @for (trademark of item.trademarks; track trademark.externalId) {
                          <button
                            class="flex w-full items-center gap-2 rounded-md border px-2 py-1 text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
                            type="button"
                            [class]="isPicked(trademark) ? 'border-primary-300 bg-primary-50' : 'border-neutral-200 hover:bg-neutral-50'"
                            (click)="pick(trademark)"
                          >
                            <mat-icon
                              class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!"
                              [class]="isPicked(trademark) ? 'text-primary-600' : 'text-neutral-300'"
                              >{{ isPicked(trademark) ? "radio_button_checked" : "radio_button_unchecked" }}</mat-icon
                            >

                            <span class="flex min-w-0 flex-1 flex-col">
                              <span class="min-w-0 truncate text-sm text-neutral-700">{{ trademark.name }}</span>
                              <span class="min-w-0 truncate text-sm text-neutral-400">{{ trademark.manufacturer }}</span>
                            </span>

                            <span class="flex shrink-0 flex-col items-end">
                              <span class="text-sm font-semibold text-neutral-600 tabular-nums">{{ trademark.price }}</span>
                              <span class="text-sm text-neutral-400 tabular-nums">{{
                                "guaranteeV2.message.packagesCount" | translate: { count: trademark.displayRequiredQuantity }
                              }}</span>
                            </span>
                          </button>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </li>
        }

        <!-- Inside the scroller, so going on with the list is where the list runs out. -->
        @if (hiddenCount()) {
          <li>
            <button
              class="w-full rounded-lg border border-dashed border-neutral-200 px-2 py-1.5 text-sm font-semibold text-neutral-500 transition-colors duration-150 hover:border-primary-300 hover:bg-primary-50/60 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
              type="button"
              (click)="showMore()"
            >
              {{ "guaranteeV2.action.showMoreOffers" | translate: { count: hiddenCount() } }}
            </button>
          </li>
        }
      </ul>
    }
  }

  <!-- The one map: the surface a place is taken on, and the surface the answers arrive on. It
       outlives every state above it, so a search that found nothing still shows where it looked. -->
  @if (mapLive()) {
    <div class="relative">
      <guarantees-location-map
        class="h-96"
        pickable
        [named]="false"
        [shown]="showMap()"
        [markers]="markers()"
        [centre]="searchArea.area()"
        [preview]="searchArea.preview()"
        [selectedId]="chosenId()"
        [busy]="loading() && !awaitingArea()"
        [busyLabel]="'guaranteeV2.message.searchingPharmacies' | translate"
        (selected)="chooseMarker($event)"
        (picked)="searchArea.placePoint($event)"
      ></guarantees-location-map>

      <!-- The pin taken, read where it was taken: the counter, its hours, and either the choice it
           now holds or the reason it cannot hold one. -->
      @if (showMap()) {
        @if (focusedPharmacy(); as pharmacy) {
          <div class="absolute right-12 bottom-2 left-2 z-10 flex flex-col gap-1.5 rounded-xl border border-neutral-100 bg-neutral-0 p-2.5 shadow-lg">
            <div class="flex items-start gap-2">
              @if (pharmacy.logoUrl) {
                <img
                  class="h-8 w-8 shrink-0 rounded-md border border-neutral-100 bg-neutral-0 object-contain p-0.5"
                  [src]="pharmacy.logoUrl"
                  alt=""
                  #pharmacyCardLogo
                  (error)="pharmacyCardLogo.hidden = true"
                />
              }

              <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                <span class="min-w-0 truncate text-sm font-semibold text-neutral-700">{{ pharmacy.pharmacyName }}</span>
                <span class="min-w-0 truncate text-sm text-neutral-500">{{ pharmacy.pharmacyAddress }}</span>

                <span class="flex flex-wrap items-center gap-x-2 text-sm text-neutral-400">
                  <span class="tabular-nums">{{ "guaranteeV2.message.distanceMeters" | translate: { count: pharmacy.distance } }}</span>
                  <span [class]="pharmacy.isOpenNow ? 'text-success-600' : ''">{{ pharmacy.scheduleLabel }}</span>
                </span>
              </span>

              <button
                class="shrink-0 rounded-full p-0.5 text-neutral-400 transition-colors duration-150 hover:bg-neutral-100 hover:text-neutral-600 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
                type="button"
                [attr.aria-label]="'guaranteeV2.action.close' | translate"
                (click)="dismissCard()"
              >
                <mat-icon class="h-4! w-4! text-base! leading-4!">close</mat-icon>
              </button>
            </div>

            @if (focusedRefusal(); as refusal) {
              <span class="flex items-center gap-1 text-sm text-neutral-400">
                <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">block</mat-icon>
                {{ refusal | translate }}
              </span>
            } @else if (pharmacy.id === chosenId()) {
              <div class="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
                <span class="flex items-center gap-1 text-sm font-semibold text-success-600">
                  <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">check_circle</mat-icon>
                  {{ "guaranteeV2.label.chosen" | translate }}
                </span>

                <!-- The card's one step onward: the list, standing on the row already unfolded to its trademarks. -->
                <button
                  class="shrink-0 rounded-full bg-primary-600 px-3 py-1 text-sm font-semibold text-neutral-0 transition-colors duration-150 hover:bg-primary-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
                  type="button"
                  (click)="view.set('list')"
                >
                  {{ "guaranteeV2.action.reviewDrugs" | translate }}
                </button>
              </div>
            }
          </div>
        }
      }
    </div>
  }
</div>
